import { useState } from 'react';
import { ArrowLeft, Filter } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import {
  getCategoryById,
  getServicesByCategory,
  getVendorById,
} from '../data/fixtures';
import { Button, PageSection, ServiceCard, Tag } from '../shell/ui';

export function CategoryScreen() {
  const { route, navigate, back } = useRouter();
  const categoryId = route.params?.categoryId || 'renovation';
  const category = getCategoryById(categoryId);
  const services = getServicesByCategory(categoryId);
  const [activeSub, setActiveSub] = useState<string | null>(null);

  if (!category) {
    return (
      <PageSection>
        <div style={{ textAlign: 'center', padding: '4rem 0' }}>
          <h1>Category not found</h1>
          <Button onClick={() => navigate('browse')}>Browse categories</Button>
        </div>
      </PageSection>
    );
  }

  const filtered = activeSub
    ? services.filter((s) => s.subcategoryId === activeSub)
    : services;

  return (
    <>
      {/* Editorial header */}
      <section
        style={{
          position: 'relative',
          height: '420px',
          background: `linear-gradient(180deg, rgba(28, 27, 26, 0) 40%, rgba(28, 27, 26, 0.75) 100%), url(${category.image}) center/cover no-repeat`,
          borderBottom: '1px solid var(--color-border-light)',
        }}
      >
        <div
          style={{
            maxWidth: '1240px',
            margin: '0 auto',
            padding: '2rem 1.5rem',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <button
            onClick={back}
            style={{
              all: 'unset',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.875rem',
              background: 'rgba(255,255,255,0.92)',
              color: 'var(--color-text-primary)',
              border: '1px solid rgba(255,255,255,0.6)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              width: 'fit-content',
              backdropFilter: 'blur(10px)',
            }}
          >
            <ArrowLeft size={14} /> Back
          </button>

          <div style={{ color: 'var(--color-text-inverse)', maxWidth: '720px' }}>
            <div
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                opacity: 0.8,
                marginBottom: '0.875rem',
              }}
            >
              Category · {category.serviceCount} services
            </div>
            <h1
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
                margin: 0,
                marginBottom: '1rem',
              }}
            >
              {category.name}
            </h1>
            <p
              style={{
                fontSize: '1.125rem',
                lineHeight: 1.55,
                opacity: 0.9,
                maxWidth: '560px',
                margin: 0,
              }}
            >
              {category.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Sub-category filter bar */}
      <PageSection paddingY="2rem" background="var(--color-background-surface)">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.875rem',
              fontSize: '0.8125rem',
              color: 'var(--color-text-muted)',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            <Filter size={13} /> Filter by
          </div>
          <button
            onClick={() => setActiveSub(null)}
            style={{
              all: 'unset',
              cursor: 'pointer',
              padding: '0.5rem 0.875rem',
              background: !activeSub ? 'var(--color-text-primary)' : 'var(--color-background-card)',
              color: !activeSub ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
              border: '1px solid var(--color-border-light)',
              borderRadius: '999px',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            All
          </button>
          {category.subcategories.map((sub) => (
            <button
              key={sub.id}
              onClick={() => setActiveSub(sub.id)}
              style={{
                all: 'unset',
                cursor: 'pointer',
                padding: '0.5rem 0.875rem',
                background: activeSub === sub.id ? 'var(--color-text-primary)' : 'var(--color-background-card)',
                color: activeSub === sub.id ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                border: '1px solid var(--color-border-light)',
                borderRadius: '999px',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {sub.name} <span style={{ opacity: 0.55, marginLeft: '0.25rem' }}>({sub.count})</span>
            </button>
          ))}
        </div>
      </PageSection>

      {/* Services grid */}
      <PageSection paddingY="3rem">
        {filtered.length === 0 ? (
          <div
            style={{
              padding: '4rem 2rem',
              textAlign: 'center',
              background: 'var(--color-background-card)',
              border: '1px solid var(--color-border-light)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: '1.5rem',
                fontWeight: 400,
                margin: 0,
                marginBottom: '0.75rem',
              }}
            >
              No matches yet
            </h3>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
              We haven’t surfaced a service in this sub-category yet. Post a request and we’ll route it to matching vendors.
            </p>
            <Button onClick={() => navigate('request')}>Post a request</Button>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.25rem',
            }}
            className="cat-services-grid"
          >
            {filtered.map((s) => {
              const vendor = getVendorById(s.vendorId)!;
              return (
                <ServiceCard
                  key={s.id}
                  image={s.heroImage}
                  title={s.title}
                  vendorName={vendor.name}
                  vendorAvatar={vendor.avatar}
                  rating={s.rating}
                  reviewCount={s.reviewCount}
                  priceFrom={s.priceFrom}
                  onClick={() => navigate('service', { serviceId: s.id })}
                />
              );
            })}
          </div>
        )}
        <style>{`
          @media (max-width: 980px) { .cat-services-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 620px) { .cat-services-grid { grid-template-columns: 1fr !important; } }
        `}</style>

        {/* Tag row */}
        {filtered.length > 0 && (
          <div style={{ marginTop: '3rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {Array.from(new Set(filtered.flatMap((s) => s.tags))).map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </div>
        )}
      </PageSection>
    </>
  );
}
